import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import moment from "moment";
import Typography from "components/basic/typography";
import { Booking } from "../../../calendar/booking/types";
import { AvailabilityData } from "../../../calendar/setAvailability/types";
import { availableSlots, Slot } from "../../helpers/availabilitySlots";

interface TimeSlotPickerProps {
  date: string;
  durationMinutes: number;
  availability: AvailabilityData | null | undefined;
  bookings: Booking[];
  excludeBookingId?: string;
  value?: string; // selected slot "from"
  onSelect: (slot: Slot) => void;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  date,
  durationMinutes,
  availability,
  bookings,
  excludeBookingId,
  value,
  onSelect,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");

  const slots = useMemo(() => {
    if (!availability || !date) {
      return [];
    }
    return availableSlots(date, durationMinutes, availability, bookings, {
      excludeBookingId,
    });
  }, [availability, date, durationMinutes, bookings, excludeBookingId]);

  if (!availability) {
    return (
      <Typography
        variant="caption"
        style={{ color: theme.palette.text.secondary }}
      >
        {t("slots.no_availability")}
      </Typography>
    );
  }

  if (slots.length === 0) {
    return (
      <Typography
        variant="caption"
        style={{ color: theme.palette.text.secondary }}
      >
        {t("slots.none")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
      }}
    >
      {slots.map((slot) => {
        const active = value === slot.from;
        return (
          <Box
            key={slot.from}
            component="button"
            type="button"
            onClick={() => onSelect(slot)}
            sx={{
              cursor: "pointer",
              textAlign: "center",
              py: 0.75,
              borderRadius: 1,
              fontSize: "0.85rem",
              border: `1px solid ${
                active ? theme.palette.warning.main : theme.palette.divider
              }`,
              backgroundColor: active
                ? `${theme.palette.warning.main}1a`
                : theme.palette.background.paper,
              fontWeight: active ? 600 : 400,
              "&:hover": { borderColor: theme.palette.warning.main },
            }}
          >
            {moment(slot.from, "HH:mm").format("h:mm A")}
          </Box>
        );
      })}
    </Box>
  );
};

export default TimeSlotPicker;
